import { router } from "@inertiajs/react"
import { ExternalLink } from "lucide-react"
import { useState } from "react"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { partnerSettingsStripeAccountPath } from "@/routes"

// IMP-194: Stripe Connect card. Three states keyed off the two booleans
// the backend passes us:
//
//   !connected                       → "Connect Stripe" CTA
//   connected && !payoutsReady       → "Action needed" pill + the live
//                                       list of what Stripe is still
//                                       waiting on, plus a "Finish setup
//                                       in Stripe" link. We use OAuth
//                                       Standard accounts, which own their
//                                       own dashboard — onboarding is
//                                       completed there, not via a
//                                       re-run of our OAuth flow.
//   connected && payoutsReady        → green "Connected" pill +
//                                       Disconnect action. Account is
//                                       cleared to accept payments and
//                                       receive payouts.
//
// "Connect Stripe" POSTs the #create endpoint via a native HTML form (NOT
// Inertia's router.post). Inertia can't follow cross-origin redirects —
// the server's 302 to connect.stripe.com just bounces the user back to
// the current page — so we submit a real form instead, including the CSRF
// token via the rails meta tag.
//
// Standard accounts (vs. the old Express setup) own their own dashboard
// at dashboard.stripe.com, so there's no Imperative-mediated Manage
// link — partners go straight to Stripe.

// Stripe's requirement keys are machine-y ("business_profile.url"). Map
// the ones we expect to friendly labels; fall back to a humanized tail
// for anything unmapped. tos_acceptance.date/.ip both collapse to one
// line, hence the de-dupe in the component.
const REQUIREMENT_LABELS: Record<string, string> = {
  "business_profile.url": "Business website",
  "business_profile.product_description": "Description of your business",
  "business_profile.support_phone": "Support phone number",
  "business_profile.mcc": "Industry category",
  "tos_acceptance.date": "Accept Stripe's terms of service",
  "tos_acceptance.ip": "Accept Stripe's terms of service",
  external_account: "Bank account for payouts",
}

function humanizeRequirements(keys: string[]): string[] {
  const labels = keys.map(
    (key) =>
      REQUIREMENT_LABELS[key] ??
      (key.split(".").pop() ?? key).replace(/_/g, " "),
  )
  return Array.from(new Set(labels))
}

export default function StripeConnectCard({
  connected,
  payoutsReady,
  requirements = [],
}: {
  connected: boolean
  payoutsReady: boolean
  requirements?: string[]
}) {
  const outstanding = humanizeRequirements(requirements)
  // CSRF token from Rails' meta tag. Could be threaded through Inertia
  // shared props, but the meta tag is canonical and always present.
  const csrfToken =
    typeof document !== "undefined"
      ? (document
          .querySelector('meta[name="csrf-token"]')
          ?.getAttribute("content") ?? "")
      : ""
  const [showDisconnect, setShowDisconnect] = useState(false)

  function handleDisconnect() {
    router.delete(partnerSettingsStripeAccountPath())
    setShowDisconnect(false)
  }

  // Submit button posted inside a real <form>. Native form POST instead
  // of Inertia's router.post because Inertia can't follow the server's
  // 302 redirect to connect.stripe.com (cross-origin) — same class of
  // bug as IMP-176's OAuth flows. The hidden authenticity_token comes
  // from the CSRF meta tag Rails injects on every render.
  const connectForm = (label: string) => (
    <form
      action={partnerSettingsStripeAccountPath()}
      method="POST"
      className="contents"
    >
      <input type="hidden" name="authenticity_token" value={csrfToken} />
      <Button type="submit" size="sm" variant="outline" className="rounded-sm">
        {label}
      </Button>
    </form>
  )

  return (
    <div>
      <div className="mb-3">
        <h2 className="text-sm font-semibold">Payouts</h2>
      </div>

      <div className="bg-muted/30 flex items-start justify-between gap-4 rounded-sm border px-4 py-3">
        {connected && payoutsReady ? (
          <>
            <div className="flex items-center gap-2 text-sm">
              <span className="font-medium">Stripe</span>
              <Badge variant="operational">Connected</Badge>
            </div>

            <div className="flex gap-2">
              <Button
                asChild
                size="sm"
                variant="outline"
                className="rounded-sm"
              >
                <a
                  href="https://dashboard.stripe.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open Stripe
                  <ExternalLink className="ml-1 size-3" />
                </a>
              </Button>

              <Button
                size="sm"
                variant="outline"
                className="text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive rounded-sm"
                onClick={() => setShowDisconnect(true)}
              >
                Disconnect
              </Button>
            </div>
          </>
        ) : connected ? (
          <>
            <div className="flex flex-col gap-1 text-sm">
              <div className="flex items-center gap-2">
                <span className="font-medium">Stripe</span>
                <Badge variant="warning">Action needed</Badge>
              </div>
              {outstanding.length > 0 ? (
                <>
                  <span className="text-muted-foreground">
                    Stripe needs a few more details before payouts can start.
                    Finish these in your Stripe dashboard:
                  </span>
                  <ul className="text-muted-foreground mt-1 list-disc space-y-0.5 pl-4">
                    {outstanding.map((label) => (
                      <li key={label}>{label}</li>
                    ))}
                  </ul>
                </>
              ) : (
                <span className="text-muted-foreground">
                  Stripe is still verifying your account — this can take a
                  little while.
                </span>
              )}
            </div>

            <div className="flex shrink-0 gap-2">
              <Button
                asChild
                size="sm"
                variant="outline"
                className="rounded-sm"
              >
                <a
                  href="https://dashboard.stripe.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Finish setup in Stripe
                  <ExternalLink className="ml-1 size-3" />
                </a>
              </Button>

              <Button
                size="sm"
                variant="outline"
                className="text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive rounded-sm"
                onClick={() => setShowDisconnect(true)}
              >
                Disconnect
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-col text-sm">
              <span className="font-medium">No payout account</span>
              <span className="text-muted-foreground">
                Connect an existing Stripe account, or sign up for a new one
              </span>
            </div>

            {connectForm("Connect Stripe")}
          </>
        )}
      </div>

      <AlertDialog open={showDisconnect} onOpenChange={setShowDisconnect}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect Stripe</AlertDialogTitle>
            <AlertDialogDescription>
              This revokes Imperative&rsquo;s access to your Stripe account and
              stops tracking it locally. Your Stripe account itself, along with
              any pending payouts and payout history, stays put on Stripe. You
              can reconnect at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDisconnect}
              className="bg-destructive hover:bg-destructive/90 text-white"
            >
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
